import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, G } from './_cnet';

// Khamis-Roche % of adult height reached at each age (whole years, boys and girls)
const PCT_BOY = { 2: 49.5, 3: 53.8, 4: 58.0, 5: 61.8, 6: 65.2, 7: 69.0, 8: 72.0, 9: 75.0, 10: 78.4, 11: 81.1, 12: 84.2, 13: 87.3, 14: 91.5, 15: 96.1, 16: 98.3, 17: 99.3, 18: 100 };
const PCT_GIRL = { 2: 52.8, 3: 57.0, 4: 61.8, 5: 66.2, 6: 70.3, 7: 74.0, 8: 77.5, 9: 80.7, 10: 84.4, 11: 88.4, 12: 92.9, 13: 96.5, 14: 98.3, 15: 99.1, 16: 99.6, 17: 99.8, 18: 100 };

function lerpPct(age, table) {
  const a = Math.max(2, Math.min(18, Number(age)));
  const lo = Math.floor(a);
  const hi = Math.min(18, lo + 1);
  const t = a - lo;
  return (table[lo] * (1 - t) + table[hi] * t) / 100;
}

function fmtHt(inches) {
  const ft = Math.floor(inches / 12);
  const inch = (inches % 12);
  return `${ft}' ${inch.toFixed(1)}"`;
}

// Height Calculator — Calculator.net-style, matches user reference image
export default function HeightCalc() {
  const [tab, setTab] = useState('adult');
  const TABS = [
    ['adult', 'Predicting Adult Height'],
    ['parents', "Based on Parents' Heights Only"],
    ['convert', 'Height Converter'],
  ];
  return (
    <CalcShell look="cnet" showActions={false} title="Height Calculator" subtitle="Predict a child's adult height using the Khamis-Roche method, mid-parental estimate, or convert between units.">
      <div className="flex gap-2 mb-0 flex-wrap">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`height-tab-${v}`}
            className={`cnet-tab ${tab === v ? 'active' : ''}`}
            onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>
      <div className="cnet-tab-body">
        {tab === 'adult' && <KhamisRocheSection />}
        {tab === 'parents' && <MidParentalSection />}
        {tab === 'convert' && <ConvertSection />}
      </div>
    </CalcShell>
  );
}

/* ---------- Sub 1: Khamis-Roche (child + parents) ---------- */
function KhamisRocheSection() {
  const [unit, setUnit] = useState('us'); // us | metric (Other Units removed per request)
  const [age, setAge] = useState(5.2);
  const [gender, setGender] = useState('boy');
  // US
  const [cFt, sCFt] = useState(3), [cIn, sCIn] = useState(8);
  const [cLb, sCLb] = useState(40);
  const [mFt, sMFt] = useState(5), [mIn, sMIn] = useState(5);
  const [fFt, sFFt] = useState(5), [fIn, sFIn] = useState(10);
  // Metric
  const [cCm, sCCm] = useState(112.5);
  const [cKg, sCKg] = useState(18);
  const [mCm, sMCm] = useState(165);
  const [fCm, sFCm] = useState(178);
  const [res, setRes] = useState(null);

  const calc = () => {
    let childH, mothH, fathH; // inches
    if (unit === 'us') {
      childH = Number(cFt) * 12 + Number(cIn);
      mothH = Number(mFt) * 12 + Number(mIn);
      fathH = Number(fFt) * 12 + Number(fIn);
    } else {
      childH = Number(cCm) / 2.54;
      mothH = Number(mCm) / 2.54;
      fathH = Number(fCm) / 2.54;
    }
    const pct = lerpPct(age, gender === 'boy' ? PCT_BOY : PCT_GIRL);
    const kr = childH / pct; // Khamis-Roche projection
    const bias = gender === 'boy' ? 2.5 : -2.5;
    const mph = (mothH + fathH) / 2 + bias;
    // Blend: 60% Khamis-Roche + 40% mid-parental for stability
    const predicted = 0.6 * kr + 0.4 * mph;
    setRes({ predicted, kr, mph, pctReached: pct * 100 });
  };
  const clear = () => setRes(null);

  return (
    <>
      <p className="text-sm text-slate-700 mb-3">This is a children's adult height prediction calculator based on a linear regression analysis method or parent's height.</p>
      <div className="cnet-panel">
        <div className="flex gap-0 mb-3">
          {[['us', 'US Units'], ['metric', 'Metric Units']].map(([v, l]) => (
            <button key={v} data-testid={`height-adult-unit-${v}`}
              className={`cnet-tab ${unit === v ? 'active' : ''}`}
              onClick={() => setUnit(v)}>{l}</button>
          ))}
        </div>
        <CNetRow label="Child's Age" threeCol={false}>
          <input data-testid="height-adult-age" className="cnet-input" type="number" step="0.1" value={age} onChange={e => setAge(e.target.value)} />
        </CNetRow>
        <div className="cnet-row-2col"><div className="cnet-label">Child's Gender</div>
          <div className="flex gap-6 items-center pt-1">
            <label className="flex items-center gap-1"><input type="radio" checked={gender === 'boy'} onChange={() => setGender('boy')} /> male</label>
            <label className="flex items-center gap-1"><input type="radio" checked={gender === 'girl'} onChange={() => setGender('girl')} /> female</label>
          </div>
        </div>
        {unit === 'us' ? (
          <>
            <div className="cnet-row" style={{ gridTemplateColumns: '180px 1fr 1fr' }}>
              <div className="cnet-label">Child's Height</div>
              <div className="flex gap-1"><input data-testid="height-adult-cft" className="cnet-input" type="number" value={cFt} onChange={e => sCFt(e.target.value)} /><span className="text-sm text-slate-500 pt-2">feet</span></div>
              <div className="flex gap-1"><input data-testid="height-adult-cin" className="cnet-input" type="number" value={cIn} onChange={e => sCIn(e.target.value)} /><span className="text-sm text-slate-500 pt-2">inches</span></div>
            </div>
            <CNetRow label="Child's Weight" threeCol={false}>
              <div className="flex gap-1"><input data-testid="height-adult-clb" className="cnet-input" type="number" value={cLb} onChange={e => sCLb(e.target.value)} /><span className="text-sm text-slate-500 pt-2">pounds</span></div>
            </CNetRow>
            <div className="cnet-row" style={{ gridTemplateColumns: '180px 1fr 1fr' }}>
              <div className="cnet-label">Mother's Height</div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={mFt} onChange={e => sMFt(e.target.value)} /><span className="text-sm text-slate-500 pt-2">feet</span></div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={mIn} onChange={e => sMIn(e.target.value)} /><span className="text-sm text-slate-500 pt-2">inches</span></div>
            </div>
            <div className="cnet-row" style={{ gridTemplateColumns: '180px 1fr 1fr' }}>
              <div className="cnet-label">Father's Height</div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={fFt} onChange={e => sFFt(e.target.value)} /><span className="text-sm text-slate-500 pt-2">feet</span></div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={fIn} onChange={e => sFIn(e.target.value)} /><span className="text-sm text-slate-500 pt-2">inches</span></div>
            </div>
          </>
        ) : (
          <>
            <CNetRow label="Child's Height" threeCol={false}><div className="flex gap-1"><input className="cnet-input" type="number" value={cCm} onChange={e => sCCm(e.target.value)} /><span className="text-sm text-slate-500 pt-2">cm</span></div></CNetRow>
            <CNetRow label="Child's Weight" threeCol={false}><div className="flex gap-1"><input className="cnet-input" type="number" value={cKg} onChange={e => sCKg(e.target.value)} /><span className="text-sm text-slate-500 pt-2">kg</span></div></CNetRow>
            <CNetRow label="Mother's Height" threeCol={false}><div className="flex gap-1"><input className="cnet-input" type="number" value={mCm} onChange={e => sMCm(e.target.value)} /><span className="text-sm text-slate-500 pt-2">cm</span></div></CNetRow>
            <CNetRow label="Father's Height" threeCol={false}><div className="flex gap-1"><input className="cnet-input" type="number" value={fCm} onChange={e => sFCm(e.target.value)} /><span className="text-sm text-slate-500 pt-2">cm</span></div></CNetRow>
          </>
        )}
        <CNetActions onCalc={calc} onClear={clear} testId="height-adult" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="height-adult-result">
            Predicted adult height: <span className="text-[#4c7b25]">{fmtHt(res.predicted)}</span> ({G(res.predicted * 2.54, 1)} cm)
          </div>
          <table className="cnet-info-table mt-3">
            <tbody>
              <tr><td>Khamis-Roche projection</td><td>{fmtHt(res.kr)} ({G(res.kr * 2.54, 1)} cm)</td></tr>
              <tr><td>Mid-parental estimate</td><td>{fmtHt(res.mph)} ({G(res.mph * 2.54, 1)} cm)</td></tr>
              <tr><td>% of adult height reached at this age</td><td>{G(res.pctReached, 1)}%</td></tr>
            </tbody>
          </table>
          <div className="text-xs text-slate-500 mt-3">Predicted = 0.60 × (child height ÷ % adult at age) + 0.40 × mid-parental height. Actual results depend on genetics, health and nutrition.</div>
        </CNetResult>
      )}
    </>
  );
}

/* ---------- Sub 2: Mid-parental (parents' heights only) ---------- */
function MidParentalSection() {
  const [unit, setUnit] = useState('us');
  const [motherFt, setMotherFt] = useState(5);
  const [motherIn, setMotherIn] = useState(2);
  const [motherCm, setMotherCm] = useState(157);
  const [fatherFt, setFatherFt] = useState(5);
  const [fatherIn, setFatherIn] = useState(10);
  const [fatherCm, setFatherCm] = useState(178);
  const [gender, setGender] = useState('boy');
  const [res, setRes] = useState(null);
  const calc = () => {
    let m, f;
    if (unit === 'metric') { m = Number(motherCm) / 2.54; f = Number(fatherCm) / 2.54; }
    else { m = Number(motherFt) * 12 + Number(motherIn); f = Number(fatherFt) * 12 + Number(fatherIn); }
    const boy = (m + f) / 2 + 2.5;
    const girl = (m + f) / 2 - 2.5;
    setRes({ boy, girl, gender });
  };
  const clear = () => setRes(null);
  return (
    <>
      <p className="text-sm text-slate-700 mb-3">This calculator uses the parents' height only. It can be used to predict the future heights of unborn children or very young infants.</p>
      <div className="cnet-panel">
        <div className="flex gap-0 mb-3">
          {[['us', 'US Units'], ['metric', 'Metric Units']].map(([v, l]) => (
            <button key={v} data-testid={`height-parents-unit-${v}`}
              className={`cnet-tab ${unit === v ? 'active' : ''}`}
              onClick={() => setUnit(v)}>{l}</button>
          ))}
        </div>
        {unit === 'us' ? (
          <>
            <div className="cnet-row" style={{ gridTemplateColumns: '180px 1fr 1fr' }}>
              <div className="cnet-label">Mother's Height</div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={motherFt} onChange={e => setMotherFt(e.target.value)} /><span className="text-sm text-slate-500 pt-2">feet</span></div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={motherIn} onChange={e => setMotherIn(e.target.value)} /><span className="text-sm text-slate-500 pt-2">inches</span></div>
            </div>
            <div className="cnet-row" style={{ gridTemplateColumns: '180px 1fr 1fr' }}>
              <div className="cnet-label">Father's Height</div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={fatherFt} onChange={e => setFatherFt(e.target.value)} /><span className="text-sm text-slate-500 pt-2">feet</span></div>
              <div className="flex gap-1"><input className="cnet-input" type="number" value={fatherIn} onChange={e => setFatherIn(e.target.value)} /><span className="text-sm text-slate-500 pt-2">inches</span></div>
            </div>
          </>
        ) : (
          <>
            <CNetRow label="Mother's Height"><input className="cnet-input" type="number" value={motherCm} onChange={e => setMotherCm(e.target.value)} /><span className="text-sm text-slate-500 pt-2">cm</span></CNetRow>
            <CNetRow label="Father's Height"><input className="cnet-input" type="number" value={fatherCm} onChange={e => setFatherCm(e.target.value)} /><span className="text-sm text-slate-500 pt-2">cm</span></CNetRow>
          </>
        )}
        <div className="cnet-row-2col"><div className="cnet-label">Child's Gender</div>
          <select className="cnet-select" value={gender} onChange={e => setGender(e.target.value)}>
            <option value="boy">Boy</option>
            <option value="girl">Girl</option>
          </select>
        </div>
        <CNetActions onCalc={calc} onClear={clear} testId="height-parents" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="height-parents-result">
            Predicted adult height for {res.gender === 'boy' ? 'boy' : 'girl'}: <span className="text-[#4c7b25]">{fmtHt(res.gender === 'boy' ? res.boy : res.girl)}</span> ({G((res.gender === 'boy' ? res.boy : res.girl) * 2.54, 1)} cm)
          </div>
          <div className="text-sm text-slate-700 mt-2">Boy estimate: <b>{fmtHt(res.boy)}</b> · Girl estimate: <b>{fmtHt(res.girl)}</b></div>
          <div className="text-xs text-slate-500 mt-3">Steps: mid-parental height = (mother + father) / 2 ± 2.5 in (+ for boy, − for girl).</div>
        </CNetResult>
      )}
    </>
  );
}

/* ---------- Sub 3: Height Converter ---------- */
function ConvertSection() {
  const UNITS = { in: 0.0254, ft: 0.3048, cm: 0.01, m: 1, mm: 0.001 };
  const [val, setVal] = useState(70);
  const [u, setU] = useState('in');
  const [res, setRes] = useState(null);
  const calc = () => setRes({ meters: Number(val) * UNITS[u] });
  return (
    <>
      <div className="cnet-panel">
        <CNetRow label="Value">
          <input className="cnet-input" type="number" step="any" value={val} onChange={e => setVal(e.target.value)} />
          <select className="cnet-select" value={u} onChange={e => setU(e.target.value)}>{Object.keys(UNITS).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <CNetActions onCalc={calc} onClear={() => setRes(null)} testId="height-convert" />
      </div>
      {res && (
        <CNetResult>
          <table className="data-table" data-testid="height-convert-result">
            <tbody>
              {Object.entries(UNITS).map(([k, f]) => (
                <tr key={k}><td>{k}</td><td>{G(res.meters / f, 4)}</td></tr>
              ))}
              <tr><td>ft + in</td><td>{Math.floor((res.meters / 0.0254) / 12)}' {((res.meters / 0.0254) % 12).toFixed(1)}"</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}
